<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LeadsAPI from 'dashboard/api/leads';
import Button from 'dashboard/components-next/button/Button.vue';
import { formatLeadCurrency } from '../helper';
import DashboardWidget from './DashboardWidget.vue';

const props = defineProps({
  pipelineId: { type: [Number, String], default: null },
  currency: { type: String, default: 'USD' },
  assigneeId: { type: [Number, String], default: '' },
  period: { type: String, default: 'today' }, // controlado desde el header
  // Estado de filtros del tablero: la card cuyo filtro está aplicado se ve
  // seleccionada, y re-clickearla lo quita (toggle).
  statusFilter: { type: String, default: '' },
  hasPendingTask: { type: String, default: '' },
  createdPeriod: { type: String, default: '' },
});
// navigateAgenda → { preset } ; navigateBoard → { ...filters }. Los listeners
// del padre siguen en kebab (@navigate-board): Vue los normaliza.
const emit = defineEmits(['navigateAgenda', 'navigateBoard']);

// Las cards reflejan el ESTADO de los filtros, no el historial de clicks:
// "Ganado" manda si status=won (aunque el rango venga de "Nuevos"); "Nuevos"
// = rango de creación activo sin status won.
const wonActive = computed(() => props.statusFilter === 'won');
const newActive = computed(
  () => !!props.createdPeriod && props.statusFilter !== 'won'
);
const withoutTaskActive = computed(() => props.hasPendingTask === 'false');

// Toggle: card activa → click = limpiar los filtros de widget del tablero
// (mismo semántico "reemplazar" que ya tiene clickear otra card).
const toggleBoardFilter = (isActive, filters) =>
  emit('navigateBoard', isActive ? {} : filters);

const { t } = useI18n();
const data = ref(null);
const hasError = ref(false);
const showForecast = ref(false);

const fmt = value => formatLeadCurrency(value, props.currency);

// Si falla, se muestra un estado de error con "Reintentar" — antes el panel
// desaparecía en silencio (¿apagado o roto?). Mientras recarga se conserva la
// data anterior (sin parpadeo); el skeleton solo aplica a la primera carga.
const load = async () => {
  if (!props.pipelineId) return;
  hasError.value = false;
  try {
    const { data: res } = await LeadsAPI.dashboard({
      pipeline_id: props.pipelineId,
      period: props.period,
      assignee_id: props.assigneeId,
    });
    data.value = res.payload;
  } catch {
    hasError.value = true;
  }
};

watch(() => [props.pipelineId, props.assigneeId, props.period], load, {
  immediate: true,
});
defineExpose({ reload: load });
</script>

<template>
  <div class="flex flex-col gap-2">
    <!-- Error: decirlo y ofrecer la salida, no desaparecer en silencio. Si ya
         hay datos en pantalla (falló una RE-carga), los chips siguen visibles
         debajo — datos viejos + aviso es más honesto que ocultarlos. -->
    <div
      v-if="hasError"
      class="flex items-center gap-2 rounded-lg border border-n-weak bg-n-solid-1 p-2.5 text-xs text-n-slate-11"
    >
      <span class="flex-shrink-0 i-lucide-cloud-off size-4 text-n-slate-10" />
      {{ t('LEADS.WIDGETS.LOAD_ERROR') }}
      <Button sm slate faded :label="t('LEADS.WIDGETS.RETRY')" @click="load" />
    </div>

    <!-- Skeleton de primera carga: reserva el alto real de los chips para que
         el tablero no salte cuando llegan los datos. -->
    <div
      v-if="!data && !hasError"
      class="flex gap-2 overflow-x-auto pb-1 md:flex-wrap md:overflow-x-visible md:pb-0"
      aria-hidden="true"
    >
      <div
        v-for="i in 6"
        :key="i"
        class="h-[70px] min-w-[5.5rem] flex-shrink-0 animate-pulse rounded-lg border border-n-weak bg-n-alpha-1 md:h-[86px] md:min-w-[9rem] md:flex-1 md:flex-shrink"
      />
    </div>

    <!-- Móvil: tira horizontal scrolleable de chips. Desktop: fila que envuelve. -->
    <div
      v-if="data"
      class="flex gap-2 overflow-x-auto pb-1 md:flex-wrap md:overflow-x-visible md:pb-0"
    >
      <DashboardWidget
        :label="t('LEADS.WIDGETS.TASKS')"
        :value="data.tasks"
        icon="i-lucide-calendar-check"
        tone="blue"
        clickable
        @select="emit('navigateAgenda', { preset: 'today' })"
      />
      <DashboardWidget
        :label="t('LEADS.WIDGETS.OVERDUE')"
        :value="data.overdue"
        icon="i-lucide-alarm-clock"
        tone="red"
        :hint="t('LEADS.WIDGETS.CURRENT')"
        clickable
        @select="emit('navigateAgenda', { preset: 'overdue' })"
      />
      <DashboardWidget
        :label="t('LEADS.WIDGETS.WITHOUT_TASK')"
        :value="data.without_task"
        icon="i-lucide-circle-dashed"
        tone="amber"
        :hint="t('LEADS.WIDGETS.CURRENT')"
        clickable
        :active="withoutTaskActive"
        :title="withoutTaskActive ? t('LEADS.FILTER.REMOVE') : undefined"
        @select="
          toggleBoardFilter(withoutTaskActive, { has_pending_task: 'false' })
        "
      />
      <DashboardWidget
        :label="t('LEADS.WIDGETS.NEW')"
        :value="data.new_leads"
        icon="i-lucide-sparkles"
        tone="indigo"
        clickable
        :active="newActive"
        :title="newActive ? t('LEADS.FILTER.REMOVE') : undefined"
        @select="toggleBoardFilter(newActive, { period })"
      />
      <DashboardWidget
        :label="t('LEADS.WIDGETS.WON')"
        :value="fmt(data.won.value)"
        icon="i-lucide-trophy"
        tone="green"
        clickable
        :active="wonActive"
        :title="wonActive ? t('LEADS.FILTER.REMOVE') : undefined"
        @select="
          toggleBoardFilter(wonActive, { status: 'won', won_period: period })
        "
      />
      <DashboardWidget
        :label="t('LEADS.WIDGETS.FORECAST')"
        :value="fmt(data.forecast.value)"
        icon="i-lucide-trending-up"
        tone="indigo"
        :hint="t('LEADS.WIDGETS.CURRENT')"
        clickable
        :active="showForecast"
        @select="showForecast = !showForecast"
      />
    </div>

    <!-- Desglose del forecast: panel full-width debajo (sirve móvil y desktop). -->
    <div
      v-if="data && showForecast"
      class="rounded-lg border border-n-weak bg-n-solid-1 p-3 text-xs"
    >
      <p class="mb-2 font-semibold">
        {{ t('LEADS.WIDGETS.FORECAST_BREAKDOWN') }}
      </p>
      <div
        v-for="b in data.forecast.breakdown"
        :key="b.stage_id"
        class="flex justify-between gap-2 py-0.5"
      >
        <span class="truncate text-n-slate-11">
          {{ b.stage_name }} ({{ Math.round(b.rate * 100) }}%)
        </span>
        <span class="tabular-nums">{{ fmt(b.contribution) }}</span>
      </div>
      <p v-if="!data.forecast.breakdown.length" class="text-n-slate-10">
        {{ t('LEADS.WIDGETS.FORECAST_EMPTY') }}
      </p>
    </div>
  </div>
</template>
